import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { useCustomer, useDeleteCustomer, useInvitePortalAccess, useSetCustomerStatus } from "../../api/customers";
import { StatusTag } from "../../components/StatusTag";
import { formatBRL, formatDate } from "../../utils/money";
import { useToast } from "../../components/ToastProvider";
import { EditCustomerModal } from "./EditCustomerModal";
import { CustomerStatus } from "../../types/dtos";

const STATUS_OPTIONS: Array<{ value: CustomerStatus; label: string }> = [
  { value: "ATIVO", label: "Ativo" },
  { value: "INATIVO", label: "Inativo" },
  { value: "INADIMPLENTE", label: "Inadimplente" },
];

export function CustomerDetailDrawer({ customerId, onClose }: { customerId: number | null; onClose: () => void }) {
  const { data: customer } = useCustomer(customerId);
  const [showEdit, setShowEdit] = useState(false);
  const invitePortal = useInvitePortalAccess();
  const setStatus = useSetCustomerStatus();
  const deleteCustomer = useDeleteCustomer();
  const toast = useToast();

  async function handleInvite() {
    if (!customerId) return;
    try {
      await invitePortal.mutateAsync(customerId);
      toast("Convite enviado — o cliente vai receber um e-mail para definir a senha.");
    } catch {
      toast("Não foi possível enviar o convite.");
    }
  }

  async function handleStatusChange(status: CustomerStatus) {
    if (!customerId) return;
    try {
      await setStatus.mutateAsync({ id: customerId, status });
      toast("Situação do cliente atualizada.");
    } catch {
      toast("Não foi possível atualizar a situação.");
    }
  }

  async function handleDelete() {
    if (!customerId || !customer) return;
    if (!window.confirm(`Excluir "${customer.razaoSocial}" definitivamente? Essa ação não pode ser desfeita.`)) return;
    try {
      await deleteCustomer.mutateAsync(customerId);
      toast("Cliente excluído.");
      onClose();
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast(message ?? "Não foi possível excluir — desative em vez disso.");
    }
  }

  const contract = customer?.contracts[0];
  const resumo = customer
    ? [
      { label: "Aplicação", valor: contract?.plan.application.name ?? "—" },
      { label: "Plano", valor: contract?.plan.name ?? "—" },
      { label: "Mensalidade", valor: contract ? formatBRL(contract.monthlyValue) : "—" },
      { label: "Cliente desde", valor: formatDate(customer.sinceDate) },
    ]
    : [];

  return (
    <>
      <Modal backdrop="static" keyboard={false} show={!!customerId} onHide={onClose} centered className="nt-dialog" size="lg">
        {!customer ? (
          <Modal.Body className="text-secondary">Carregando…</Modal.Body>
        ) : (
          <>
            <Modal.Header closeButton className="border-0 pb-0">
              <Modal.Title style={{ fontSize: 20, fontFamily: "var(--font-heading)" }}>
                {customer.razaoSocial}
                <div className="nt-tabular" style={{ fontSize: 12, fontWeight: 400, color: "var(--sp-text-muted)" }}>
                  {customer.cnpjCpf}
                </div>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="d-flex flex-column gap-4">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <StatusTag status={customer.status} />
                <select
                  className="form-select form-select-sm"
                  style={{ width: 160 }}
                  value={customer.status}
                  disabled={setStatus.isPending}
                  onChange={(e) => handleStatusChange(e.target.value as CustomerStatus)}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <button type="button" className="btn btn-secondary ms-auto" style={{ fontSize: 12.5 }} onClick={() => setShowEdit(true)}>
                  Editar
                </button>
              </div>

              <div className="row g-3">
                {resumo.map((r) => (
                  <div className="col-6" key={r.label}>
                    <div style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--sp-text-muted)" }}>
                      {r.label}
                    </div>
                    <div className="nt-tabular" style={{ fontSize: 15 }}>
                      {r.valor}
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <h6 className="mb-2 pb-2" style={{ borderBottom: "1px solid var(--color-divider)", fontWeight: 700 }}>
                  Títulos
                </h6>
                {customer.receivables.length === 0 && (
                  <div className="text-secondary" style={{ fontSize: 13 }}>
                    Nenhum título ainda.
                  </div>
                )}
                <div className="d-flex flex-column gap-2">
                  {customer.receivables.map((r) => (
                    <div
                      key={r.id}
                      className="d-flex align-items-center gap-3 flex-wrap"
                      style={{ fontSize: 13, paddingBottom: "var(--space-2)", borderBottom: "1px solid var(--color-divider)" }}
                    >
                      <span className="flex-grow-1" style={{ minWidth: 100 }}>
                        {r.reference}
                      </span>
                      <span className="nt-tabular" style={{ color: "var(--sp-text-muted)" }}>
                        {formatDate(r.dueDate)}
                      </span>
                      <span className="nt-tabular" style={{ fontWeight: 700 }}>
                        {formatBRL(r.value)}
                      </span>
                      <StatusTag status={r.status} />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h6 className="mb-2 pb-2" style={{ borderBottom: "1px solid var(--color-divider)", fontWeight: 700 }}>
                  Portal do cliente
                </h6>
                {customer.portalAccount ? (
                  <div className="d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: 13 }}>
                    <StatusTag status={customer.portalAccount.status} />
                    <span className="text-secondary" style={{ minWidth: 0 }}>
                      {customer.portalAccount.email}
                      {customer.portalAccount.lastLoginAt && ` · último acesso ${formatDate(customer.portalAccount.lastLoginAt)}`}
                    </span>
                  </div>
                ) : (
                  <div className="text-secondary" style={{ fontSize: 13 }}>
                    Cliente ainda não foi convidado para o portal.
                  </div>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              {customer.portalAccount?.status !== "ACTIVE" && (
                <button type="button" className="btn btn-secondary" disabled={invitePortal.isPending} onClick={handleInvite}>
                  {customer.portalAccount ? "Reenviar convite" : "Convidar para o portal"}
                </button>
              )}
              <button
                type="button"
                className="btn btn-outline-danger me-auto"
                disabled={deleteCustomer.isPending}
                onClick={handleDelete}
              >
                Excluir cliente
              </button>
              <button type="button" className="btn btn-primary" onClick={onClose}>
                Fechar
              </button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      <EditCustomerModal customer={showEdit ? customer ?? null : null} onClose={() => setShowEdit(false)} />
    </>
  );
}
